import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutData } from '@/mocks/about';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    if (honeypotField) {
      formData.delete('phone_alt');
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9th4cd3pcjqs2fcse7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解羊宫妃那词条百科的品牌故事、使命愿景与核心价值观。我们致力于以严谨的态度、通俗的语言，为读者提供高质量的网络流行语解析和二次元文化内容。"
        keywords="羊宫妃那词条百科,品牌介绍,关于我们,网络文化,二次元百科"
        canonicalUrl="https://tjcongling.cn/about"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 羊宫妃那词条百科',
            description: aboutData.introduction.split('\n\n')[0],
            url: 'https://tjcongling.cn/about',
            about: {
              '@type': 'Organization',
              name: aboutData.brandName,
              description: aboutData.slogan,
            },
            isPartOf: {
              '@type': 'WebSite',
              name: '羊宫妃那词条百科',
              url: 'https://tjcongling.cn',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://tjcongling.cn/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
              },
            ],
          },
        ]}
      />

      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100 border-b border-foreground-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="mt-4">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                关于我们
              </h1>
              <p className="text-foreground-500 mt-2">{aboutData.slogan}</p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="prose prose-lg max-w-none prose-p:text-foreground-700 prose-p:leading-relaxed">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                品牌介绍
              </h2>
              {aboutData.introduction.split('\n\n').map((paragraph, i) => (
                <p key={i} className="mb-4 text-foreground-700 leading-relaxed">{paragraph}</p>
              ))}
              <blockquote className="border-l-4 border-primary-400 pl-5 py-2 my-6 bg-background-100 rounded-r-lg">
                <p className="text-foreground-700 italic">{aboutData.mission}</p>
              </blockquote>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-10" style={{ fontFamily: 'var(--font-heading)' }}>
              我们的价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {aboutData.values.map((value, i) => (
                <div key={i} className="bg-white rounded-lg border border-foreground-100 p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${value.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{value.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-10" style={{ fontFamily: 'var(--font-heading)' }}>
              数据与信誉
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-14">
              {aboutData.reputation.stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-2">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <h3 className="text-lg font-semibold text-foreground-950 text-center mb-8">用户评价</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aboutData.reputation.testimonials.map((t, i) => (
                <div key={i} className="bg-white rounded-lg border border-foreground-100 p-6">
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4 italic">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{t.author}</p>
                    <p className="text-xs text-foreground-400">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-14 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              联系我们
            </h2>
            <p className="text-foreground-500 text-center mb-10 text-sm">
              有任何问题或建议？欢迎给我们留言
            </p>

            {formStatus === 'success' ? (
              <div className="text-center py-12 bg-white rounded-lg border border-foreground-100">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-2">提交成功！</h3>
                <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复。</p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-6 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  发送新消息
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-white rounded-lg border border-foreground-100 p-6 md:p-8 space-y-5"
              >
                {formStatus === 'error' && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                    {formError}
                  </div>
                )}

                <div className="honeypot-wrapper">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-foreground-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors bg-background-50"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-foreground-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors bg-background-50"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2.5 text-sm border border-foreground-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors bg-background-50"
                    placeholder="请输入留言主题"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 text-sm border border-foreground-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors bg-background-50 resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1.5 text-right">最多 500 字符</p>
                </div>

                <button
                  type="submit"
                  className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  发送留言
                </button>
              </form>
            )}

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <p className="text-xs text-foreground-400 mb-1">电子邮箱</p>
                <p className="text-sm text-foreground-700">{aboutData.contact.email}</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <p className="text-xs text-foreground-400 mb-1">地址</p>
                <p className="text-sm text-foreground-700">{aboutData.contact.address}</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-weibo-line text-lg"></i>
                </div>
                <p className="text-xs text-foreground-400 mb-1">社交媒体</p>
                <p className="text-sm text-foreground-700">{aboutData.contact.weibo}</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}